import type { ReadonlyRefOrGetter } from "@vueuse/core";

import type { EventNameDetails } from "~/types/Event";

/**
 * Composable to get information about an auction event
 */
export const useEventDetails = <T extends EventNameDetails>(
  event: ReadonlyRefOrGetter<T | undefined>,
): {
  // Base return type on input
  hasParticipants: Ref<boolean>;
  featuredTeam: Ref<T["participants"][number] | undefined>;
  homeTeam: Ref<T["participants"][number] | undefined>;
  awayTeam: Ref<T["participants"][number] | undefined>;
  isHomeTeamFeatured: Ref<boolean>;
  isAwayTeamFeatured: Ref<boolean>;
  eventName: Ref<string | null>;
} => {
  const hasParticipants = computed(() => !!toValue(event)?.participants.length);

  const homeTeam = computed(() =>
    toValue(event)?.participants?.find((p) => p.isHome),
  );

  const awayTeam = computed(() =>
    toValue(event)?.participants?.find((p) => !p.isHome),
  );

  const isHomeTeamFeatured = computed(
    () => !!toValue(event)?.participants?.find((p) => p.isHome)?.isFeatured,
  );

  const isAwayTeamFeatured = computed(
    () => !!toValue(event)?.participants?.find((p) => !p.isHome)?.isFeatured,
  );

  const featuredTeam = computed(() =>
    toValue(event)?.participants?.find((p) => p.isFeatured),
  );

  const eventName = computed(() => {
    const eventValue = toValue(event);

    if (eventValue?.forceEventName) {
      return eventValue.name;
    }

    const eventNameParts = [];
    if (homeTeam.value) {
      eventNameParts.push(homeTeam.value.name);

      if (awayTeam.value) {
        eventNameParts.push("-");
      }
    }

    if (awayTeam.value) {
      eventNameParts.push(awayTeam.value.name);
    }

    if (eventNameParts.length > 0) {
      return eventNameParts.join(" ");
    }

    return null;
  });

  return {
    hasParticipants,
    featuredTeam,
    homeTeam,
    awayTeam,
    isHomeTeamFeatured,
    isAwayTeamFeatured,
    eventName,
  };
};
